/**
 * SAP Business ByDesign onboarding-push domain types.
 *
 * The push follows the proven runbook in SAPIntegration/
 * SAP_LIVE_Onboarding_Queries.md: an ordered saga of SOAP + OData calls
 * that creates a brand-new employee in SAP LIVE. These types are shared
 * by the API (mapping resolution, saga, admin routes) and the web admin
 * UI (mapping editor, review screen, run log).
 *
 * Two catalogues drive everything:
 *   - SAP_TARGET_FIELDS: every SAP input we can populate, each declaring
 *     where its value comes from ('form' = admin-mapped onboarding form
 *     field, 'person' = fixed people column, 'employment' = the person's
 *     SAP employment record with role-level defaults). Forms are
 *     HR-authored at runtime, so form-sourced fields are resolved through
 *     the sap_field_mappings table, never hard-coded to a field key.
 *   - SAP_PUSH_STEPS: the saga order. The runbook's Step 3 (fresh
 *     ChangeStateID query) is a recorded sub-action of each write step,
 *     not a step of its own.
 */

export const SAP_PUSH_STEPS = [
  'hire',
  'time_profile',
  'work_email',
  'bank_lookup',
  'bank_create',
  'bank_details',
  'ni_number',
  'next_of_kin',
] as const;
export type SapPushStep = (typeof SAP_PUSH_STEPS)[number];

export const SAP_PUSH_STEP_LABELS: Record<SapPushStep, string> = {
  hire: 'Hire employee',
  time_profile: 'Time profile (schedule + holiday)',
  work_email: 'Work email',
  bank_lookup: 'Bank lookup',
  bank_create: 'Create bank (only if missing)',
  bank_details: 'Bank details',
  ni_number: 'NI number',
  next_of_kin: 'Next of kin',
};

export type SapRunStatus = 'running' | 'succeeded' | 'failed' | 'dry_run_complete';
export type SapStepStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'warning' // committed with SAP log severity 2 (e.g. the SEPA/IBAN note)
  | 'failed'
  | 'skipped';

/** Where a target field's value comes from. */
export type SapFieldSource = 'form' | 'person' | 'employment';

/**
 * Value semantics, used for validation and for the mapping editor UI.
 * 'code' fields carry codeOptions and need a value_map translating the
 * source form's option values to SAP codes. 'country' accepts an ISO
 * 3166-1 alpha-2 code or a nationality name the resolver can translate.
 */
export type SapFieldKind =
  | 'text'
  | 'date'
  | 'email'
  | 'number'
  | 'code'
  | 'country'
  | 'sort_code'
  | 'account_number'
  | 'ni_number';

/** Sub-field selector when a mapping points at a composite address field. */
export type SapAddressPart = 'line1' | 'line2' | 'city' | 'county' | 'postcode';

export interface SapCodeOption {
  value: string;
  label: string;
}

export interface SapTargetFieldDef {
  id: string;
  label: string;
  step: SapPushStep;
  required: boolean;
  source: SapFieldSource;
  kind: SapFieldKind;
  /** SAP code list for 'code' fields (drives the value_map editor). */
  codeOptions?: SapCodeOption[];
  /** For fields that may map onto a composite address form field. */
  addressPart?: SapAddressPart;
  /** For source 'person': which people column supplies the value. */
  personColumn?: 'start_date' | 'email' | 'personal_email' | 'first_name' | 'last_name';
}

/** GenderCode per the runbook (Step 1). */
export const SAP_GENDER_CODES: SapCodeOption[] = [
  { value: '1', label: 'Male' },
  { value: '2', label: 'Female' },
  { value: '3', label: 'Unknown / not stated' },
];

/** MaritalStatusCode per the runbook (Step 1). */
export const SAP_MARITAL_CODES: SapCodeOption[] = [
  { value: '1', label: 'Single' },
  { value: '2', label: 'Married' },
  { value: '3', label: 'Widowed' },
  { value: '4', label: 'Divorced' },
  { value: '5', label: 'Separated' },
  { value: '6', label: 'Domestic partnership' },
];

/**
 * FormOfAddressCode. 0001/0002 are SAP-standard; Z003/Z004 are tenant
 * fine-tuning confirmed on TEST via the Step 0.3 code-list query
 * (13.07.2026) — re-verify on LIVE before the first production push.
 */
export const SAP_TITLE_CODES: SapCodeOption[] = [
  { value: '0001', label: 'Ms' },
  { value: '0002', label: 'Mr' },
  { value: 'Z003', label: 'Miss' },
  { value: 'Z004', label: 'Mrs' },
];

export const SAP_TARGET_FIELDS: SapTargetFieldDef[] = [
  // Step 1 — hire
  { id: 'hire.hire_date', label: 'Hire date', step: 'hire', required: true, source: 'person', kind: 'date', personColumn: 'start_date' },
  { id: 'hire.given_name', label: 'First name', step: 'hire', required: true, source: 'person', kind: 'text', personColumn: 'first_name' },
  { id: 'hire.family_name', label: 'Last name', step: 'hire', required: true, source: 'person', kind: 'text', personColumn: 'last_name' },
  { id: 'hire.middle_name', label: 'Middle name', step: 'hire', required: false, source: 'form', kind: 'text' },
  // Required in practice: the TEST tenant blocks hires without a title
  // (severity 3 "Title missing"), so readiness must catch it first. Forms
  // rarely ask for a title; mapping this to the Gender question with
  // per-option code translations (male -> Mr etc.) is the intended setup.
  { id: 'hire.title_code', label: 'Title', step: 'hire', required: true, source: 'form', kind: 'code', codeOptions: SAP_TITLE_CODES },
  { id: 'hire.gender_code', label: 'Gender', step: 'hire', required: true, source: 'form', kind: 'code', codeOptions: SAP_GENDER_CODES },
  { id: 'hire.birth_date', label: 'Date of birth', step: 'hire', required: true, source: 'form', kind: 'date' },
  { id: 'hire.birth_place', label: 'Place of birth', step: 'hire', required: false, source: 'form', kind: 'text' },
  { id: 'hire.marital_status_code', label: 'Marital status', step: 'hire', required: false, source: 'form', kind: 'code', codeOptions: SAP_MARITAL_CODES },
  { id: 'hire.nationality', label: 'Nationality', step: 'hire', required: true, source: 'form', kind: 'country' },
  { id: 'hire.address_street', label: 'Home address: street', step: 'hire', required: false, source: 'form', kind: 'text', addressPart: 'line1' },
  { id: 'hire.address_city', label: 'Home address: city', step: 'hire', required: false, source: 'form', kind: 'text', addressPart: 'city' },
  { id: 'hire.address_postcode', label: 'Home address: postcode', step: 'hire', required: false, source: 'form', kind: 'text', addressPart: 'postcode' },
  { id: 'hire.personal_email', label: 'Personal email', step: 'hire', required: false, source: 'person', kind: 'email', personColumn: 'personal_email' },
  { id: 'hire.mobile_phone', label: 'Mobile number', step: 'hire', required: false, source: 'form', kind: 'text' },
  { id: 'hire.home_phone', label: 'Home phone', step: 'hire', required: false, source: 'form', kind: 'text' },
  // Step 4 — work email
  { id: 'work_email.uri', label: 'Work email', step: 'work_email', required: true, source: 'person', kind: 'email', personColumn: 'email' },
  // Steps 5/6 — bank
  { id: 'bank.sort_code', label: 'Sort code', step: 'bank_details', required: true, source: 'form', kind: 'sort_code' },
  { id: 'bank.account_number', label: 'Account number', step: 'bank_details', required: true, source: 'form', kind: 'account_number' },
  { id: 'bank.holder_name', label: 'Name on account', step: 'bank_details', required: true, source: 'form', kind: 'text' },
  { id: 'bank.bank_name', label: 'Bank name (for creating a missing bank)', step: 'bank_create', required: false, source: 'form', kind: 'text' },
  { id: 'bank.iban', label: 'IBAN (silences the SEPA warning)', step: 'bank_details', required: false, source: 'form', kind: 'text' },
  // Step 7 — NI number
  { id: 'ni.number', label: 'National Insurance number', step: 'ni_number', required: true, source: 'form', kind: 'ni_number' },
  // Step 8 — next of kin (all individually optional in SAP)
  { id: 'nok.name', label: 'Next of kin: name', step: 'next_of_kin', required: false, source: 'form', kind: 'text' },
  { id: 'nok.relationship', label: 'Next of kin: relationship', step: 'next_of_kin', required: false, source: 'form', kind: 'text' },
  { id: 'nok.mobile', label: 'Next of kin: mobile', step: 'next_of_kin', required: false, source: 'form', kind: 'text' },
  { id: 'nok.phone', label: 'Next of kin: phone', step: 'next_of_kin', required: false, source: 'form', kind: 'text' },
  { id: 'nok.address_line1', label: 'Next of kin: address line 1', step: 'next_of_kin', required: false, source: 'form', kind: 'text', addressPart: 'line1' },
  { id: 'nok.town', label: 'Next of kin: town', step: 'next_of_kin', required: false, source: 'form', kind: 'text', addressPart: 'city' },
  { id: 'nok.county', label: 'Next of kin: county', step: 'next_of_kin', required: false, source: 'form', kind: 'text', addressPart: 'county' },
  { id: 'nok.email', label: 'Next of kin: email', step: 'next_of_kin', required: false, source: 'form', kind: 'email' },
];

/** Fields the mapping editor manages (source 'form' only). */
export const SAP_MAPPABLE_FIELDS: SapTargetFieldDef[] = SAP_TARGET_FIELDS.filter(
  (f) => f.source === 'form',
);

export function sapTargetField(id: string): SapTargetFieldDef | undefined {
  return SAP_TARGET_FIELDS.find((f) => f.id === id);
}

/**
 * The person's SAP employment record: HR decisions SAP requires that no
 * onboarding form captures. Stored per person (metadata.sap.employment)
 * with role-level defaults (roles.sap_defaults); effective value is
 * person override ?? role default.
 */
export const SAP_CONTRACT_TYPES = ['permanent', 'temporary'] as const;
export type SapContractType = (typeof SAP_CONTRACT_TYPES)[number];

export interface SapEmploymentValues {
  /** OrganisationalCentreID, e.g. 'CLHCC1101' (from the reference cache). */
  org_centre_id?: string;
  /** SAP JobID (free text until the LIVE job list is discovered). */
  job_id?: string;
  /** Drives WorkAgreement TypeCode AND the LeavingDate rule. */
  contract_type?: SapContractType;
  /** AgreedWorkingHoursRate per week, e.g. 40. */
  weekly_hours?: number;
  /** Working time model ModelId, e.g. 'Z3_40HOURS' (reference cache). */
  working_time_model?: string;
}

export const SAP_EMPLOYMENT_FIELDS: { key: keyof SapEmploymentValues; label: string; required: boolean }[] = [
  { key: 'org_centre_id', label: 'Organisational centre', required: true },
  { key: 'job_id', label: 'SAP job ID', required: true },
  { key: 'contract_type', label: 'Contract type', required: true },
  { key: 'weekly_hours', label: 'Weekly hours', required: true },
  { key: 'working_time_model', label: 'Working time model', required: true },
];

/** One row of the admin-managed mapping (SAP field -> form field). */
export interface SapMapping {
  sap_field: string;
  form_key: string | null;
  field_key: string | null;
  /** For 'code' fields: source option value -> SAP code. */
  value_map: Record<string, string>;
  updated_at?: string;
}

export type SapMappingHealthStatus = 'ok' | 'unmapped' | 'form_missing' | 'field_missing' | 'option_drift';

export interface SapMappingHealth {
  sap_field: string;
  status: SapMappingHealthStatus;
  detail?: string;
}

/** Where a resolved value actually came from (review-screen provenance). */
export type SapValueOrigin = 'form' | 'person' | 'employment_person' | 'employment_role' | 'missing';

export interface ResolvedSapField {
  field: string;
  label: string;
  step: SapPushStep;
  required: boolean;
  /** The value as it will be sent to SAP (post value_map / formatting). */
  value: string | null;
  origin: SapValueOrigin;
  /** Present when the value exists but cannot be used as-is. */
  problem?: string;
}

export interface SapReadinessReport {
  ready: boolean;
  missing: { field: string; label: string; reason: string }[];
}

export type SapReferenceKind = 'org_centre' | 'working_time_model' | 'job';

export interface SapReferenceEntry {
  kind: SapReferenceKind;
  code: string;
  label: string;
}

export interface SapPushRunSummary {
  id: string;
  person_id: string;
  status: SapRunStatus;
  dry_run: boolean;
  started_by: string | null;
  sap_employee_id: string | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface SapPushStepSummary {
  id: string;
  step_key: SapPushStep;
  seq: number;
  status: SapStepStatus;
  sap_severity: number | null;
  change_state_before: string | null;
  change_state_after: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  /** Dry runs return the rendered payload inline (nothing was sent). */
  rendered_payload?: string;
}
